/**
 * Single shared HTMLAudioElement slot for TTS codewords [[sound:…]], [[soundasync:…]], [[stopsound]], [[fadeoutsound]].
 * Only one clip at a time; starting a new sound stops the previous.
 */

import {
  getCatalogUrlForSoundKey,
  isKnownTtsSoundKey,
  normalizeTtsSoundKey
} from './tts-sound-catalog';
import { DEFAULT_TTS_FADEOUT_MS } from './tts-queue';

let currentAudio: HTMLAudioElement | null = null;
/** Incremented on every stop so stale audio events do not call `done()` or corrupt state. */
let playGeneration = 0;
let fadeInterval: ReturnType<typeof setInterval> | null = null;

function getSoundsBase(): string {
  if (typeof window === 'undefined') return '';
  const w = window as unknown as { __LECTURA_TTS_SOUNDS_BASE__?: string };
  if (typeof w.__LECTURA_TTS_SOUNDS_BASE__ === 'string' && w.__LECTURA_TTS_SOUNDS_BASE__.trim()) {
    return w.__LECTURA_TTS_SOUNDS_BASE__.trim();
  }
  try {
    const fromData = document.documentElement?.getAttribute('data-tts-sounds-base')?.trim();
    return fromData || '';
  } catch {
    return '';
  }
}

/**
 * Resolve a sound reference: full URL, site-relative path, catalog URL, or base + key.mp3 for known catalog keys.
 */
export function resolveTtsSoundUrl(ref: string): string | null {
  const r = String(ref || '').trim();
  if (!r) return null;
  if (/^https?:\/\//i.test(r) || r.startsWith('/')) return r;

  const norm = normalizeTtsSoundKey(r);
  if (!norm) return null;

  const base = getSoundsBase();
  // When a base is configured, treat any short key as a hosted file name.
  // This lets decks override the built-in catalog and host their own sounds.
  if (base) {
    const sep = base.endsWith('/') ? '' : '/';
    const hasExt = /\.[a-z0-9]{2,5}$/i.test(norm);
    return `${base}${sep}${norm}${hasExt ? '' : '.mp3'}`;
  }

  const fromCat = getCatalogUrlForSoundKey(norm);
  if (fromCat) return fromCat;

  if (!isKnownTtsSoundKey(norm)) {
    console.warn(
      `[Lectura TTS] sound: unknown key "${r}". Add it to tts-sound-catalog.ts or use a full https URL or /path.`
    );
    return null;
  }

  console.warn(
    '[Lectura TTS] sound: set URLs in tts-sound-catalog.ts or window.__LECTURA_TTS_SOUNDS_BASE__ / data-tts-sounds-base for short keys.'
  );
  return null;
}

function clearFadeInterval(): void {
  if (fadeInterval) {
    clearInterval(fadeInterval);
    fadeInterval = null;
  }
}

/** Stop any TTS-managed sound (slide change, TTS off, or [[stopsound]]). */
export function stopTtsSound(): void {
  clearFadeInterval();
  playGeneration++;
  const a = currentAudio;
  currentAudio = null;
  if (!a) return;
  try {
    a.pause();
    a.removeAttribute('src');
    a.load();
  } catch {
    /* ignore */
  }
}

const DEFAULT_FADE_MS = 600;

/**
 * Fade out current TTS sound over `ms` then stop. If nothing is playing, `done` runs immediately.
 */
export function fadeOutTtsSound(ms: number, done: () => void): void {
  clearFadeInterval();
  const a = currentAudio;
  if (!a) {
    done();
    return;
  }
  const myGen = playGeneration;
  const startVol = a.volume;
  const dur = Math.max(100, Math.min(15000, Number.isFinite(ms) ? ms : DEFAULT_TTS_FADEOUT_MS));
  const steps = Math.max(8, Math.min(48, Math.floor(dur / 32)));
  const dt = dur / steps;
  let step = 0;
  fadeInterval = setInterval(() => {
    step++;
    if (myGen !== playGeneration || currentAudio !== a) {
      clearFadeInterval();
      done();
      return;
    }
    if (step >= steps) {
      clearFadeInterval();
      stopTtsSound();
      done();
      return;
    }
    a.volume = startVol * (1 - step / steps);
  }, dt);
}

function applyVolume(a: HTMLAudioElement, volume?: number): void {
  if (volume === undefined) {
    a.volume = 1;
    return;
  }
  a.volume = Math.max(0, Math.min(1, volume));
}

export function playTtsSoundBlocking(key: string, done: () => void, volume?: number): void {
  const url = resolveTtsSoundUrl(key);
  if (!url) {
    done();
    return;
  }
  stopTtsSound();
  const myGen = playGeneration;
  const a = new Audio(url);
  applyVolume(a, volume);
  currentAudio = a;
  const finish = (): void => {
    if (myGen !== playGeneration) return;
    if (currentAudio === a) currentAudio = null;
    done();
  };
  a.addEventListener('ended', finish);
  a.addEventListener('error', finish);
  void a.play().catch(finish);
}

/** Start sound without blocking the TTS queue; still replaces any currently playing TTS sound. */
export function playTtsSoundNonBlocking(key: string, volume?: number): void {
  const url = resolveTtsSoundUrl(key);
  if (!url) return;
  stopTtsSound();
  const myGen = playGeneration;
  const a = new Audio(url);
  applyVolume(a, volume);
  currentAudio = a;
  const cleanup = (): void => {
    if (myGen !== playGeneration) return;
    if (currentAudio === a) currentAudio = null;
  };
  a.addEventListener('ended', cleanup);
  a.addEventListener('error', cleanup);
  void a.play().catch(cleanup);
}
